import { useTranslation } from 'react-i18next'
import {
  forwardRef,
  useEffect,
  useId,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type KeyboardEvent,
  type ReactNode,
} from 'react'
import { cn } from '@/ui/shared/utils/cn'
import { ds } from './tokens'

export type ProfileMenuItem = {
  key: string
  label: ReactNode
  icon?: ReactNode
  onClick?: () => void
  danger?: boolean
  disabled?: boolean
  dividerBefore?: boolean
}

export interface ProfileTriggerProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  /** Avatar image or icon inside the trigger */
  children: ReactNode
  menuItems: ProfileMenuItem[]
  menuLabel?: string
  align?: 'start' | 'end'
}

const ProfileTrigger = forwardRef<HTMLButtonElement, ProfileTriggerProps>(
  (
    {
      children,
      menuItems,
      menuLabel,
      align = 'end',
      className,
      'aria-label': ariaLabel,
      ...props
    },
    ref
  ) => {
    const { t } = useTranslation()
    const resolvedMenuLabel = menuLabel ?? t('shell.profile.userMenu')
    const resolvedAriaLabel = ariaLabel ?? t('shell.profile.userProfile')
    const [open, setOpen] = useState(false)
    const rootRef = useRef<HTMLDivElement>(null)
    const menuId = useId()

    useEffect(() => {
      if (!open) return

      const handlePointer = (event: MouseEvent) => {
        if (!rootRef.current?.contains(event.target as Node)) {
          setOpen(false)
        }
      }

      const handleEscape = (event: globalThis.KeyboardEvent) => {
        if (event.key === 'Escape') setOpen(false)
      }

      document.addEventListener('mousedown', handlePointer)
      document.addEventListener('keydown', handleEscape)
      return () => {
        document.removeEventListener('mousedown', handlePointer)
        document.removeEventListener('keydown', handleEscape)
      }
    }, [open])

    const handleTriggerKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
      if (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ') {
        event.preventDefault()
        setOpen(true)
      }
    }

    return (
      <div ref={rootRef} className="relative inline-flex">
        <button
          ref={ref}
          type="button"
          className={cn(ds.profileTrigger, className)}
          aria-label={resolvedAriaLabel}
          aria-haspopup="menu"
          aria-expanded={open}
          aria-controls={open ? menuId : undefined}
          onClick={() => setOpen((value) => !value)}
          onKeyDown={handleTriggerKeyDown}
          {...props}
        >
          {children}
        </button>

        {open ? (
          <div
            id={menuId}
            role="menu"
            aria-label={resolvedMenuLabel}
            className={cn(
              ds.profileMenu,
              'absolute top-full z-50 mt-2 py-1',
              align === 'end' ? 'right-0' : 'left-0'
            )}
          >
            {menuItems.map((item) => (
              <div key={item.key}>
                {item.dividerBefore ? (
                  <div className="my-1 border-t border-[var(--border)]" role="separator" />
                ) : null}
                <button
                  type="button"
                  role="menuitem"
                  disabled={item.disabled}
                  className={cn(
                    'flex min-h-11 w-full items-center gap-2 px-3 text-left text-sm',
                    'transition-colors hover:bg-[var(--muted)]',
                    'focus:outline-none focus-visible:bg-[var(--muted)]',
                    'disabled:pointer-events-none disabled:opacity-50',
                    item.danger
                      ? 'text-[var(--error-ink)]'
                      : 'text-[var(--foreground)]'
                  )}
                  onClick={() => {
                    item.onClick?.()
                    setOpen(false)
                  }}
                >
                  {item.icon ? (
                    <span className="inline-flex h-5 w-5 items-center justify-center [&>svg]:h-4 [&>svg]:w-4">
                      {item.icon}
                    </span>
                  ) : null}
                  {item.label}
                </button>
              </div>
            ))}
          </div>
        ) : null}
      </div>
    )
  }
)

ProfileTrigger.displayName = 'ProfileTrigger'

export default ProfileTrigger
